import {isPlatformServer} from '@angular/common';
import {inject, PLATFORM_ID, RESPONSE_INIT} from '@angular/core';
import {RedirectCommand, type ResolveFn, Router} from '@angular/router';
import {redirectTo404} from '@app/navigation/page-not-found/page-not-found.component';
import {DatasetResponse} from '@backend';
import {type IndexSearchResponse, IndexService} from '@middleware';
import {CommonsSearchDataset} from '@models/commons-types';
import {LandingServiceBrokerService} from '@services/landing-service-broker/landing-service-broker.service';
import {SensitivityLevel} from '@shared/labels/sensitivity-level';
import {lastValueFrom} from 'rxjs';

type ResolvedDataset = DatasetResponse &
  CommonsSearchDataset & {
    availablePermissionEmails: string[];
  };

export const privateDatasetResolver: ResolveFn<ResolvedDataset | RedirectCommand> = async (route, _state) => {
  if (isPlatformServer(inject(PLATFORM_ID))) return undefined;

  const indexService = inject(IndexService);
  const landingServiceBroker = inject(LandingServiceBrokerService);
  const router = inject(Router);
  const response = inject(RESPONSE_INIT, {optional: true});

  const datasetId = route.paramMap.get('datasetId');

  let indexSearchResponse: IndexSearchResponse;

  try {
    indexSearchResponse = await lastValueFrom(
      indexService.searchDatasetsInIndex({id: {terms: datasetId}}, {public_view: false}),
    );

    if (!indexSearchResponse.results[0]) {
      return redirectTo404(router, response);
    }
  } catch (err) {
    console.log(err);
    return redirectTo404(router, response);
  }

  const publicView = indexSearchResponse.results[0] as unknown as CommonsSearchDataset;

  if (!publicView) {
    console.error('Dataset missing or malformed in elasticsearch index', {
      datasetId,
      results: indexSearchResponse.results,
    });
  }

  const serviceInstanceMap = await landingServiceBroker.getByOrganizationName(publicView.storage_organization.name);

  try {
    const dataset = await lastValueFrom(serviceInstanceMap.DatasetsService.getDataset(publicView.id));

    if (
      dataset.scheduled_for_deletion_on != publicView.scheduled_for_deletion_on ||
      dataset.dataset_file_versions.length != publicView.dataset_file_versions.length ||
      dataset.dataset_file_versions.find(dfv => dfv.is_latest_and_not_deleted)?.file_version?.id !=
        publicView.dataset_file_versions.find(dfv => dfv.is_latest_and_not_deleted)?.file_version?.id ||
      dataset.dataset_file_versions
        .find(dfv => dfv.is_latest_and_not_deleted)
        ?.file_version?.file_hipaa_warnings?.some(
          hw =>
            !publicView.dataset_file_versions
              .find(dfv => dfv.is_latest_and_not_deleted)
              ?.file_version?.file_hipaa_warnings?.includes(hw),
        )
    )
      await indexService.syncDatasetInIndex(dataset.id, {storage_organization: dataset.storage_organization.name});

    if (dataset.scheduled_for_deletion_on) {
      return redirectTo404(router, response);
    }

    let availablePermissionEmails: string[] = [];

    if (dataset.sensitivity_level?.id === SensitivityLevel.HSD && dataset.study_irb_number) {
      const projectIds = dataset.associated_projects.map(p => p.project.id);

      const investigatorResponses = await Promise.all(
        projectIds.map(projectId =>
          lastValueFrom(
            serviceInstanceMap.IrbProtocolsService.getInvestigatorsByStudyIRBNumber(dataset.study_irb_number, {
              project_id: projectId,
            }),
          ),
        ),
      );

      availablePermissionEmails = Array.from(
        new Set(investigatorResponses.flatMap(response => response.map(user => user.email))),
      );
    }

    return {
      ...publicView,
      ...dataset,
      availablePermissionEmails,
    };
  } catch (err) {
    console.log(err);
    return redirectTo404(router, response);
  }
};
